import { useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  addressShown,
  doctorPath,
  doctorPhoneDialled,
  doctorPhoneShown,
  parseClientId,
  readClientCaseFilter,
  writeClientCaseFilter,
  type Doctor,
  type DoctorPatient,
} from '@/domain';
import { useDeleteDoctor, useDoctor, useSimilarDoctors } from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { AddressBlock } from '@/ui/data/AddressBlock';
import { Loading } from '@/ui/feedback/Loading';
import { ChevronRightIcon, DoctorIcon, PencilIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { Tabs } from '@/ui/primitives/Tabs';
import { toast } from '@/ui/primitives/toastStore';
import { CaseInPlace } from '@/features/orders/CaseInPlace';
import { DoctorPrices } from '@/features/payments/DoctorPrices';
import { useConfirmAction } from '../shell/useConfirmAction';
import { DoctorCases } from './ClientCases';
import { readFailure, statusOf } from './clientRefusal';
import { mailHref, telHref, webHref } from './contactLinks';
import { DoctorFormSheet } from './DoctorFormSheet';
import { DoctorPatients } from './DoctorPatients';
import type { PickedDoctor } from './DoctorPicker';
import { MergeDoctorSheet } from './MergeDoctorSheet';
import {
  BackLink,
  ProfileFailed,
  ProfileLoading,
  ProfileNotFound,
  Stat,
  type BackTarget,
} from './ProfileParts';
import { useAddressParams } from './useAddressParams';
import styles from './ClientProfile.module.css';

/**
 * The tables under a doctor's record, and the key the address names the others
 * by: the cases, the patients, and for a holder of pricing the doctor's own
 * prices (the lab's request of 17 October 2026, item 11).
 */
type ProfileTab = 'cases' | 'patients' | 'prices';
const TAB_PARAM = 'tab';

const BACK: BackTarget = { to: '/doctors', label: strings.clients.backToDoctors };

const notFound = (
  <ProfileNotFound
    back={BACK}
    icon={<DoctorIcon />}
    heading={strings.clients.doctorNotFound}
    detail={strings.clients.doctorNotFoundDetail}
  />
);

/**
 * A doctor's profile. The request of 6 October 2026 (second), items 4 and 5.
 *
 * Who the doctor is and how the lab reaches them, then the two things the
 * record ties together: the doctor's patients, each a step from their own
 * profile, and the doctor's cases, each a step from the case. That is the path
 * the client asked for, from a doctor to a patient to a case and back.
 *
 * Since the request of 6 October 2026 (third): the doctor's ID stands under
 * the name; the patients are those with a case under this doctor, since a
 * patient is one record whichever doctors they are seen by, and are not added
 * from here; the doctors whose name looks like this one are listed as possible
 * duplicates, and whoever may merge can merge one of them, or any doctor, into
 * this one; and the name is changed only by a holder of `renameClients`.
 *
 * An address that names no record is a record that was not found, said without
 * asking the server; so is one the server no longer has.
 *
 * Since the request of 6 October 2026 (fifth): the number is in its
 * international form and the address is the label its country's post writes,
 * each still the text typed before where nobody has put it right yet; the
 * patients and the cases are two tabs, each a table searched, filtered,
 * ordered and paged by the server, with its state in the page's address; and a
 * case opens here, over the page, instead of leaving for Orders.
 */
export function DoctorProfileScreen() {
  const { id: param } = useParams<{ id: string }>();
  const id = parseClientId(param);
  // Keyed by the record: another doctor's profile starts on its own first page of cases.
  return id === null ? notFound : <DoctorProfile key={id} id={id} />;
}

function DoctorProfile({ id }: { id: string }) {
  const navigate = useNavigate();
  const permissions = useSession().data?.permissions ?? [];
  // Three permissions, three things: the details, the name, and merging.
  const mayManage = permissions.includes('manageClients');
  const mayRename = permissions.includes('renameClients');
  const mayMerge = permissions.includes('mergeDoctors');
  const doctor = useDoctor(id);
  const similar = useSimilarDoctors(id);
  const remove = useDeleteDoctor();
  const ask = useConfirmAction();
  const nameId = useId();
  const [editing, setEditing] = useState(false);
  /*
   * Which of the two tables is showing is in the address, `?tab=patients`,
   * and the cases are what an address that says nothing shows. Changed in
   * place, as the filters under it are: Back leaves the doctor.
   */
  const [params, replace] = useAddressParams();
  const prices = permissions.includes('pricing');
  const askedTab = params.get(TAB_PARAM);
  const tab: ProfileTab =
    askedTab === 'patients' ? 'patients' : askedTab === 'prices' && prices ? 'prices' : 'cases';

  if (doctor.isPending) return <ProfileLoading back={BACK} label={strings.clients.loadingDoctor} />;
  // Gone, whatever copy is still in hand: deleted since it was read, or never there.
  if (statusOf(doctor.error) === 404) return notFound;
  // Never read. A copy that is in hand stays on screen through a refresh that failed.
  if (doctor.data === undefined) {
    return <ProfileFailed back={BACK} error={doctor.error} onRetry={() => void doctor.refetch()} />;
  }

  const record = doctor.data;

  function showTab(next: ProfileTab) {
    replace((current) => {
      const address = new URLSearchParams(current);
      if (next === 'cases') address.delete(TAB_PARAM);
      else address.set(TAB_PARAM, next);
      return address;
    });
  }

  /*
   * A patient pressed on the Patients tab: the Cases tab, narrowed to that
   * patient and to nothing else, in the order the cases were last read in. A
   * search or a status left on from before would hide cases the reader has
   * just asked to see.
   */
  function showCasesOf(patient: DoctorPatient) {
    replace((current) => {
      const address = new URLSearchParams(current);
      address.delete(TAB_PARAM);
      return writeClientCaseFilter(address, {
        sort: readClientCaseFilter(address).sort,
        patientId: patient.id,
      });
    });
  }

  /**
   * A figure pressed: the Cases tab, all of them or the active only, or the
   * Patients tab. The lab's request of 17 October 2026, item 1.
   */
  function showCases(active: boolean) {
    replace((current) => {
      const address = new URLSearchParams(current);
      address.delete(TAB_PARAM);
      return writeClientCaseFilter(address, {
        sort: readClientCaseFilter(address).sort,
        ...(active ? { status: 'active' as const } : {}),
      });
    });
  }

  function deleteRecord() {
    ask(
      strings.clients.confirmDeleteDoctor(record.name),
      () =>
        remove.mutateAsync(record.id).then(
          () => {
            // At once: the record is gone, and a profile left on screen would ask for it again.
            void navigate(BACK.to, { replace: true });
            toast(strings.clients.doctorDeleted(record.name), { tone: 'success' });
          },
          (error: unknown) => {
            // A case booked in meanwhile is why: the record is read again, and Delete goes.
            void doctor.refetch();
            throw error;
          },
        ),
      { title: strings.clients.deleteDoctorTitle, confirmLabel: strings.clients.deleteRecord },
    );
  }

  return (
    <div>
      <BackLink {...BACK} />

      <section className={styles.card} aria-labelledby={nameId}>
        <div className={styles.top}>
          <div className={styles.who}>
            <span className={styles.lbl}>{strings.clients.doctorLabel}</span>
            {/* A name may be in Arabic: it sets its own direction, as on the case screen. */}
            <h2 id={nameId} dir="auto">
              {record.name}
            </h2>
            {/* The ID: never typed, never used twice. The request of 6 October 2026 (third), item 2. */}
            <p className={styles.code}>{record.code}</p>
          </div>
          {(mayManage || mayRename) && (
            <div className={styles.actions}>
              {/* Whoever may change the name or the details: the form shows which of the two. */}
              <Button icon={<PencilIcon />} onClick={() => setEditing(true)}>
                {strings.clients.edit}
              </Button>
              {/* Only a record with no case can go: the server refuses any other. */}
              {mayManage && record.caseCount === 0 && (
                <Button disabled={remove.isPending} onClick={deleteRecord}>
                  {strings.clients.deleteRecord}
                </Button>
              )}
            </div>
          )}
        </div>

        <DoctorFacts doctor={record} />

        <div className={styles.stats}>
          <Stat label={strings.clients.statCases} value={record.caseCount} onPress={() => showCases(false)} />
          <Stat label={strings.clients.statActive} value={record.activeCaseCount} onPress={() => showCases(true)} />
          <Stat label={strings.clients.statPatients} value={record.patientCount} onPress={() => showTab('patients')} />
        </div>
      </section>

      <PossibleDuplicates
        doctor={record}
        similar={similar}
        mayMerge={mayMerge}
        onStale={() => {
          void doctor.refetch();
          void similar.refetch();
        }}
      />

      {/*
        The doctor's cases and the doctor's patients, a table each. The request
        of 6 October 2026 (fifth), section 4: a doctor may have many of both,
        so each is searched, filtered, ordered and paged by the server.
      */}
      <Tabs
        label={strings.clientCases.tabsLabel}
        current={tab}
        onSelect={showTab}
        tabs={[
          { key: 'cases', label: strings.clientCases.tabCases },
          { key: 'patients', label: strings.clientCases.tabPatients },
          ...(prices ? [{ key: 'prices' as const, label: strings.doctorPrices.tab }] : []),
        ]}
      >
        {tab === 'cases' ? (
          <DoctorCases doctor={record} />
        ) : tab === 'patients' ? (
          <DoctorPatients doctor={record} onShowCases={showCasesOf} />
        ) : (
          <DoctorPrices doctorId={record.id} doctorName={record.name} />
        )}
      </Tabs>

      {/* The case a row opened, over this page: a drawer, or on a phone a page of its own. */}
      <CaseInPlace returnLabel={record.name} />

      {editing && (
        <DoctorFormSheet
          doctor={record}
          onClose={() => setEditing(false)}
          onDone={() => setEditing(false)}
          onStale={() => void doctor.refetch()}
        />
      )}
    </div>
  );
}

/** As much of the possible duplicates' query as the block reads. */
interface SimilarQuery {
  data?: Doctor[];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/**
 * A doctor's possible duplicates, and merging. The request of 6 October 2026
 * (third), item 1: similar names became separate doctors.
 *
 * The other doctors whose name is this one's once a title, the spacing and the
 * punctuation are set aside, each with its ID, its clinic and its cases, and
 * each a step from its own profile: somebody deciding which of two records is
 * the real one wants to look at both. Drawn only when there are any, for
 * somebody who can only read.
 *
 * For a holder of `mergeDoctors` each has Merge, which merges THAT doctor into
 * the one on screen, and under the list Merge another doctor into this one,
 * for a duplicate the loose key does not catch (a misspelling, a maiden name).
 * So for them the block is always there. Either way ends in the one
 * confirmation. Nobody without the permission is shown a way to merge.
 */
function PossibleDuplicates({
  doctor,
  similar,
  mayMerge,
  onStale,
}: {
  doctor: Doctor;
  similar: SimilarQuery;
  mayMerge: boolean;
  onStale: () => void;
}) {
  const headingId = useId();
  // The doctor a merge is being asked about, or that one is being searched for, or nothing.
  const [merging, setMerging] = useState<PickedDoctor | 'choose' | null>(null);
  const others = similar.data ?? [];

  // Nothing to say to somebody who could do nothing about it.
  if (!mayMerge && others.length === 0) return null;

  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <div className={styles.sectionHead}>
        <h3 id={headingId}>
          {strings.clients.duplicatesHeading}
          {similar.data !== undefined && <span>{others.length.toLocaleString()}</span>}
        </h3>
      </div>

      {similar.isPending ? (
        <Loading label={strings.clients.loadingSimilar} />
      ) : similar.isError ? (
        <p className={styles.error} role="alert">
          {readFailure(similar.error)}
          <Button onClick={() => void similar.refetch()}>{strings.errors.retry}</Button>
        </p>
      ) : others.length === 0 ? (
        <p className={styles.hint}>{strings.clients.noSimilar}</p>
      ) : (
        <>
          <p className={styles.note}>{strings.clients.duplicatesHint}</p>
          <ul className={styles.list}>
            {others.map((other) => (
              <li key={other.id} className={styles.dupe}>
                <Link className={styles.row} to={doctorPath(other.id)}>
                  <span className={styles.rowText}>
                    <b dir="auto">{other.name}</b>
                    <span>
                      <span className={styles.id}>{other.code}</span>
                      {other.clinic !== null && ` · ${other.clinic}`}
                    </span>
                  </span>
                  <span className={styles.rowCount}>
                    {strings.clients.casesCounted(other.caseCount)}
                  </span>
                  <ChevronRightIcon size={15} />
                </Link>
                {mayMerge && (
                  <Button
                    // Named for the doctor it merges: a list may hold several.
                    aria-label={strings.clients.mergeNamed(other.name, other.code)}
                    onClick={() => setMerging(other)}
                  >
                    {strings.clients.merge}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {mayMerge && (
        <div className={styles.foot}>
          <Button onClick={() => setMerging('choose')}>{strings.clients.mergeAnother}</Button>
        </div>
      )}

      {merging !== null && (
        <MergeDoctorSheet
          keep={doctor}
          duplicate={merging === 'choose' ? undefined : merging}
          onClose={() => setMerging(null)}
          // The profile already holds the answer: the cases that moved, the details that were filled in.
          onMerged={() => setMerging(null)}
          onStale={onStale}
        />
      )}
    </section>
  );
}

/**
 * How the lab reaches the doctor. Every one of the five reads Not set until
 * somebody fills it in, so an empty record says it is empty and not broken.
 * The number rings, the email writes, and the website opens in a tab of its
 * own, where it is told nothing about the page it came from.
 *
 * Since the request of 6 October 2026 (fifth), section 2: the number is the
 * international one where the doctor has one, and the address is the label
 * its country's post writes, line under line. A doctor nobody has edited since
 * still has the text that was typed before, shown as it was typed. Which of
 * the two a screen shows is `doctorPhoneShown` and `addressShown` in the
 * domain, the same on every screen. Where the conversion could not place the
 * old text with certainty the field says Needs review, and how it is put
 * right: by editing the doctor.
 */
function DoctorFacts({ doctor }: { doctor: Doctor }) {
  const unset = <span className={styles.unset}>{strings.clients.notSet}</span>;
  const phone = doctorPhoneShown(doctor);
  const address = addressShown(doctor);

  return (
    <dl className={styles.facts}>
      <div>
        <dt>{strings.clients.fieldClinic}</dt>
        <dd>{doctor.clinic ?? unset}</dd>
      </div>
      <div>
        <dt>{strings.clients.profilePhone}</dt>
        <dd>
          {phone === null ? (
            unset
          ) : (
            <a href={telHref(doctorPhoneDialled(doctor) ?? phone)}>{phone}</a>
          )}
          {doctor.phoneNeedsReview && <NeedsReview />}
        </dd>
      </div>
      <div>
        <dt>{strings.clients.fieldEmail}</dt>
        <dd>
          {doctor.email === null ? unset : <a href={mailHref(doctor.email)}>{doctor.email}</a>}
        </dd>
      </div>
      <div>
        <dt>{strings.clients.fieldWebsite}</dt>
        <dd>
          {doctor.website === null ? (
            unset
          ) : (
            <a href={webHref(doctor.website)} target="_blank" rel="noopener noreferrer">
              {doctor.website}
              <span className="visually-hidden"> ({strings.clients.opensInNewTab})</span>
            </a>
          )}
        </dd>
      </div>
      <div className={styles.wide}>
        <dt>{strings.clients.profileAddress}</dt>
        <dd>
          {address === null ? (
            unset
          ) : address.kind === 'lines' ? (
            <AddressBlock lines={address.lines} />
          ) : (
            // Typed before addresses had parts: as it was typed, line by line.
            <span className={styles.address}>{address.text}</span>
          )}
          {doctor.addressNeedsReview && <NeedsReview />}
        </dd>
      </div>
      <div>
        <dt>{strings.clients.latestCase}</dt>
        <dd>
          {doctor.lastCaseAt === null ? strings.clients.noCases : formatDay(doctor.lastCaseAt)}
        </dd>
      </div>
      <div>
        <dt>{strings.clients.addedOn}</dt>
        <dd>{formatDay(doctor.createdAt)}</dd>
      </div>
    </dl>
  );
}

/**
 * The mark on a number or an address the conversion left for a person: what
 * is shown is the old text, or as much of it as was certain. With the one
 * sentence that says what to do about it, since a mark alone is a question.
 */
function NeedsReview() {
  return (
    <span className={styles.review}>
      <b>{strings.clients.needsReview}</b>
      <span>{strings.clients.needsReviewHint}</span>
    </span>
  );
}
